/**
 * Recursively converts any JSON value into an array of
 * { id: string, label: string, children?: [...] } suitable for <RichTreeView>.
 *
 * ── ID STRATEGY ───────────────────────────────────────────────────────────────
 * The id is the JSON-Path from the root, e.g. "root.users[0].name".
 * • Stable across renders (unlike an incrementing counter).
 * • Readable when debugging.
 * • Guaranteed unique because the path itself is unique.
 *
 * If you prefer numeric ids or UUIDs, see the notes below.
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * @param {*} value – any JSON-serialisable value (object, array, primitive)
 * @returns {Array<{id: string, label: string, children?: any[]}>}
 */
export function jsonToTree(value, idKey, nameKey) {
  /**
   * @param {*} v         – current node value
   * @param {string} key  – the property name (undefined for root)
   * @param {string} path – accumulated JSON-Path so far
   */
  const walk = (v, key, path) => {
    // Build the next path fragment
    const thisPath =
      key === undefined
        ? 'root'
        : /^\[\d+]/.test(key)
        ? `${path}${key}` // already an array index "[3]"
        : path === '' || path === 'root'
        ? `root.${key}` // first property after root
        : `${path}.${key}`;

    // Arrays ---------------------------------------------------------------
    if (Array.isArray(v)) {
      return {
        id: thisPath,
        label: key ?? 'Array',
        children: v.map((item, i) =>
          walk(
            item,
            `[${i}] ${item[nameKey] ?? ''} - ${item[idKey] ?? ''}`,
            thisPath,
          ),
        ),
      };
    }

    // Objects --------------------------------------------------------------
    if (v !== null && typeof v === 'object') {
      return {
        id: thisPath,
        label: key ?? 'Object',
        children: Object.entries(v).map(([k, val]) => walk(val, k, thisPath)),
      };
    }

    // Primitives -----------------------------------------------------------
    const primitiveLabel =
      key !== undefined ? `${key}: ${String(v)}` : String(v);

    return { id: thisPath, label: primitiveLabel, children: [] };
  };

  // Top-level may be object, array or primitive
  return walk(value, undefined, '').children ?? [];
}
